import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Share2, X, Link2, Check, Pause, Play as PlayIcon } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Reveal } from "@/components/Reveal";
import { media } from "@/config/products";

/**
 * ProductStoryCarousel v2 — Editorial, faster, fluid.
 *
 * • 7 slides, one idea per slide.
 * • Photo slide: 2.9s. Video slide: real duration (already trimmed to ≈4s).
 * • Cross-fade + subtle scale/blur (480ms, cubic-bezier(0.22,1,0.36,1)).
 * • Progress bar via Web Animations API (transform: scaleX, GPU accelerated) —
 *   NO setInterval, NO per-frame React state, NO layout thrash.
 * • IntersectionObserver gates autoplay to when the carousel is visible.
 * • Videos: preload="none", swapped in only for the active slide → only one
 *   video is loaded / decoded / playing at any time.
 * • Deep link (?story=N) + Web Share API + share menu retained.
 * • Respects prefers-reduced-motion.
 */

const EASE = [0.22, 1, 0.36, 1];
const PHOTO_MS = 2900;
const VIDEO_FALLBACK_MS = 4000;
const TRANSITION_MS = 480;
const RESUME_AFTER_MS = 1500;

// Assets (final placeholders — swap `src` once real photography arrives).
const PHOTO_CLASSIC =
  "https://customer-assets-jai6qajn.emergentagent.net/job_diamond-lab-preview/artifacts/58c34rgw_E08C630D-7304-4907-B9D2-0CE2FF492559.png";
const PHOTO_CLASSIC_ALT =
  "https://customer-assets-jai6qajn.emergentagent.net/job_diamond-lab-preview/artifacts/i0a19dua_IMG_6881.png";
const PHOTO_MACRO =
  "https://customer-assets-jai6qajn.emergentagent.net/job_diamond-lab-preview/artifacts/u27l99yo_IMG_6880.png";

const SLIDES_META = [
  // 1 · Brilliance — hero macro video
  { type: "video", src: "/videos/story-1-brilliance.mp4", poster: "/videos/story-1-brilliance-poster.jpg" },
  // 2 · Diamond tester — macro clarity video
  { type: "video", src: "/videos/story-2-tester.mp4", poster: "/videos/story-2-tester-poster.jpg" },
  // 3 · VVS clarity — macro photo
  { type: "image", src: PHOTO_CLASSIC },
  // 4 · D color — bright photo
  { type: "image", src: PHOTO_CLASSIC_ALT },
  // 5 · 925 Sterling Silver — mounting detail
  // 5 · 925 Sterling Silver — top-down macro showing settings & mount detail
  { type: "image", src: PHOTO_MACRO },
  // 6 · Premium presentation — box
  { type: "image", src: media.packaging },
  // 7 · Designed to stand out — CTA slide
  { type: "image", src: media.classicAlt || PHOTO_CLASSIC_ALT },
];

export default function ProductStoryCarousel() {
  const { t } = useLanguage();
  const reduce = useReducedMotion();
  const localized = t.story;

  const slides = useMemo(
    () =>
      SLIDES_META.map((m, i) => ({
        ...m,
        headline: localized.slides[i]?.headline || "",
        sub: localized.slides[i]?.sub || "",
        cta: localized.slides[i]?.cta || null,
      })),
    [localized]
  );

  const [index, setIndex] = useState(() => {
    if (typeof window === "undefined") return 0;
    try {
      const params = new URLSearchParams(window.location.search);
      const raw = parseInt(params.get("story") || "", 10);
      if (Number.isFinite(raw) && raw >= 1 && raw <= SLIDES_META.length) {
        return raw - 1;
      }
    } catch (_e) { /* noop */ }
    return 0;
  });
  // Capture ONCE (synchronously, before any effect runs) whether the very first
  // URL had an explicit ?story=N > 1. Only then should the page auto-scroll to
  // the carousel — otherwise a fresh visit to "/" must land on the hero.
  const initialDeepLinkRef = useRef((() => {
    if (typeof window === "undefined") return null;
    try {
      const params = new URLSearchParams(window.location.search);
      const raw = parseInt(params.get("story") || "", 10);
      if (Number.isFinite(raw) && raw >= 2 && raw <= SLIDES_META.length) return raw;
    } catch (_e) { /* noop */ }
    return null;
  })());
  const [paused, setPaused] = useState(false);
  const [inView, setInView] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const containerRef = useRef(null);
  const shareRef = useRef(null);
  const barsRef = useRef([]);
  const activeAnimRef = useRef(null);
  const videoRef = useRef(null);
  const resumeTimerRef = useRef(null);

  // ─── Deep-link support ────────────────────────────────────────────────────
  // Only scroll the carousel into view when the ORIGINAL URL contained an
  // explicit ?story=N with N>1 (i.e. someone opened a shared link). A fresh
  // visit to "/" or "/?story=1" must always land on the hero — no auto-scroll.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!initialDeepLinkRef.current) return;
    window.setTimeout(() => {
      const el = document.querySelector("[data-testid='story-carousel']");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 400);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep URL in sync with the active slide, BUT never write the default (?story=1).
  // • index === 0 → strip the param so a first visit produces a clean "/" URL.
  // • index > 0   → write ?story=N (for share links).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (index === 0) {
      if (url.searchParams.has("story")) {
        url.searchParams.delete("story");
        window.history.replaceState({}, "", url.pathname + (url.search ? url.search : "") + url.hash);
      }
    } else {
      url.searchParams.set("story", String(index + 1));
      window.history.replaceState({}, "", url.toString());
    }
  }, [index]);

  // ─── IntersectionObserver: only autoplay when visible ─────────────────────
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) setInView(e.isIntersecting && e.intersectionRatio > 0.35);
      },
      { threshold: [0, 0.35, 0.75] }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // ─── Tab visibility ───────────────────────────────────────────────────────
  useEffect(() => {
    const onVis = () => setPaused(document.visibilityState !== "visible");
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  // ─── Advance helpers ──────────────────────────────────────────────────────
  const goTo = useCallback(
    (nextIdx) => {
      const total = slides.length;
      const clean = ((nextIdx % total) + total) % total;
      setIndex(clean);
    },
    [slides.length]
  );
  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  const prev = useCallback(() => goTo(index - 1), [goTo, index]);

  const armResume = useCallback(() => {
    if (resumeTimerRef.current) window.clearTimeout(resumeTimerRef.current);
    setPaused(true);
    resumeTimerRef.current = window.setTimeout(() => setPaused(false), RESUME_AFTER_MS);
  }, []);

  // ─── Progress-bar animation (Web Animations API) ──────────────────────────
  // Runs a single WAAPI animation on the active bar. Pauses/plays with `paused`
  // or `inView`. Fires `next()` on finish. Also drives the video sync via ended.
  useEffect(() => {
    // Reset all bars: past = full, current = empty, future = empty.
    barsRef.current.forEach((bar, i) => {
      if (!bar) return;
      // Cancel any lingering anim on this bar.
      bar.getAnimations().forEach((a) => a.cancel());
      const fill = i < index ? 1 : 0;
      bar.style.transform = `scaleX(${fill})`;
    });

    if (!inView || paused) {
      activeAnimRef.current = null;
      return;
    }

    const bar = barsRef.current[index];
    if (!bar) return;

    const current = slides[index];
    let duration = current.type === "image" ? PHOTO_MS : VIDEO_FALLBACK_MS;

    // For videos, sync duration to metadata once available.
    if (current.type === "video" && videoRef.current) {
      const v = videoRef.current;
      if (Number.isFinite(v.duration) && v.duration > 0) {
        duration = Math.min(v.duration * 1000, 4600);
      }
    }

    // Start the WAAPI animation on the active bar.
    const anim = bar.animate(
      [{ transform: "scaleX(0)" }, { transform: "scaleX(1)" }],
      {
        duration: reduce ? 300 : duration,
        easing: "linear",
        fill: "forwards",
      }
    );
    activeAnimRef.current = anim;
    anim.onfinish = () => {
      if (activeAnimRef.current === anim) next();
    };
    return () => {
      anim.onfinish = null;
      anim.cancel();
    };
  }, [index, inView, paused, reduce, slides, next]);

  // ─── Video autoplay for the active slide only ─────────────────────────────
  useEffect(() => {
    const current = slides[index];
    if (current.type !== "video") return;
    const v = videoRef.current;
    if (!v) return;
    // Attempt play; browsers require muted for autoplay.
    v.muted = true;
    v.playsInline = true;
    const p = v.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
    const onEnded = () => {
      // If the WAAPI progress animation hasn't finished yet, force-complete it
      // so bars visually catch up; then advance.
      const anim = activeAnimRef.current;
      if (anim) {
        try { anim.finish(); } catch (_) { next(); }
      } else {
        next();
      }
    };
    v.addEventListener("ended", onEnded);
    return () => v.removeEventListener("ended", onEnded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, slides]);

  // Pause/resume the active video with the carousel.
  useEffect(() => {
    const current = slides[index];
    if (current.type !== "video") return;
    const v = videoRef.current;
    if (!v) return;
    if (paused || !inView) {
      v.pause();
    } else {
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    }
  }, [paused, inView, index, slides]);

  // ─── Preload next media just-in-time ──────────────────────────────────────
  useEffect(() => {
    const nxt = slides[(index + 1) % slides.length];
    if (!nxt) return;
    if (nxt.type === "image") {
      const im = new Image();
      im.src = nxt.src;
    } else if (nxt.type === "video") {
      // Warm the connection / TLS but do not download the full body.
      const link = document.createElement("link");
      link.rel = "preload";
      link.as = "video";
      link.href = nxt.src;
      link.setAttribute("fetchpriority", "low");
      document.head.appendChild(link);
      return () => { try { document.head.removeChild(link); } catch (_) {} };
    }
  }, [index, slides]);

  // ─── Drag / keyboard controls ────────────────────────────────────────────
  const onDragEnd = useCallback(
    (_e, info) => {
      const dx = info.offset.x;
      if (Math.abs(dx) < 42) return;
      if (dx < 0) next();
      else prev();
      armResume();
    },
    [armResume, next, prev]
  );

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") { next(); armResume(); }
      else if (e.key === "ArrowLeft") { prev(); armResume(); }
      else if (e.key === " ") { e.preventDefault(); setPaused((p) => !p); }
    };
    el.addEventListener("keydown", onKey);
    return () => el.removeEventListener("keydown", onKey);
  }, [armResume, next, prev]);

  // ─── Share menu — close on outside click / Escape ────────────────────────
  useEffect(() => {
    if (!shareOpen) return;
    const onDoc = (e) => {
      if (shareRef.current && !shareRef.current.contains(e.target)) setShareOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setShareOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [shareOpen]);

  const buildShareUrl = useCallback(() => {
    if (typeof window === "undefined") return "";
    const url = new URL(window.location.href);
    url.searchParams.set("story", String(index + 1));
    url.hash = "story-carousel";
    return url.toString();
  }, [index]);

  const buildShareText = useCallback(() => {
    return `${localized.share.text}: ${slides[index]?.headline || ""}`;
  }, [index, slides, localized]);

  const handleShareClick = useCallback(async () => {
    setPaused(true);
    const url = buildShareUrl();
    const text = buildShareText();
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      try {
        await navigator.share({ title: text, text, url });
        window.setTimeout(() => setPaused(false), 800);
        return;
      } catch (_e) { /* user cancelled → fall through */ }
    }
    setShareOpen(true);
  }, [buildShareUrl, buildShareText]);

  const openChannel = useCallback((channel) => {
    const url = buildShareUrl();
    const text = buildShareText();
    const enc = encodeURIComponent;
    let target = "";
    if (channel === "whatsapp") target = `https://wa.me/?text=${enc(text + " " + url)}`;
    else if (channel === "twitter") target = `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(url)}`;
    else if (channel === "facebook") target = `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}&quote=${enc(text)}`;
    if (target) window.open(target, "_blank", "noopener,noreferrer,width=640,height=560");
    setShareOpen(false);
    window.setTimeout(() => setPaused(false), 400);
  }, [buildShareUrl, buildShareText]);

  const copyShareLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(buildShareUrl());
      setCopied(true);
      window.setTimeout(() => {
        setCopied(false); setShareOpen(false); setPaused(false);
      }, 1400);
    } catch (_) { /* clipboard blocked */ }
  }, [buildShareUrl]);

  const handleCtaClick = useCallback((href) => {
    if (!href) return;
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const current = slides[index];
  const isVideo = current?.type === "video";

  return (
    <section
      data-testid="story-section"
      className="bg-[#faf8f5] py-14 lg:py-20"
    >
      <div className="mx-auto max-w-2xl px-4 sm:px-5 lg:max-w-3xl lg:px-6">
        <Reveal className="mb-6 text-center lg:mb-8">
          <span className="eyebrow text-[11px] text-[#999]">{localized.eyebrow}</span>
        </Reveal>

        <Reveal>
          <div
            ref={containerRef}
            tabIndex={0}
            role="region"
            aria-roledescription="carousel"
            data-testid="story-carousel"
            className="group relative mx-auto w-full overflow-hidden rounded-[26px] bg-[#fafaf8] shadow-[0_18px_50px_-18px_rgba(0,0,0,0.18)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#111]/40"
            style={{ aspectRatio: "4 / 5" }}
          >
            {/* Top scrim so progress bars remain readable across light + dark slides */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 top-0 z-20 h-16 bg-gradient-to-b from-black/28 via-black/10 to-transparent"
            />

            {/* ── Progress bars (Stories) ────────────────────────────────── */}
            <div
              data-testid="story-progress"
              className="pointer-events-none absolute inset-x-0 top-0 z-30 flex gap-1 px-3 pt-3 sm:gap-1.5 sm:px-4 sm:pt-4"
            >
              {slides.map((_, i) => (
                <div
                  key={i}
                  className="h-[2.5px] flex-1 overflow-hidden rounded-full bg-white/35"
                >
                  <div
                    ref={(el) => (barsRef.current[i] = el)}
                    className="h-full w-full origin-left rounded-full bg-white will-change-transform"
                    style={{ transform: `scaleX(${i < index ? 1 : 0})` }}
                  />
                </div>
              ))}
            </div>

            {/* ── Share button + menu (top-right) ───────────────────────── */}
            <div ref={shareRef} className="absolute right-3 top-6 z-40 sm:right-4 sm:top-8">
              <button
                type="button"
                onClick={handleShareClick}
                aria-label={localized.share.button}
                aria-haspopup="menu"
                aria-expanded={shareOpen}
                data-testid="story-share-btn"
                className="press flex h-9 w-9 items-center justify-center rounded-full border border-white/25 bg-black/30 text-white shadow-[0_6px_20px_rgba(0,0,0,0.25)] backdrop-blur-xl transition-colors hover:bg-black/45 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
              >
                <Share2 className="h-4 w-4" strokeWidth={1.7} />
              </button>

              <AnimatePresence>
                {shareOpen && (
                  <motion.div
                    role="menu"
                    aria-label={localized.share.title}
                    data-testid="story-share-menu"
                    initial={reduce ? { opacity: 0 } : { opacity: 0, y: -6, scale: 0.98 }}
                    animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
                    exit={reduce ? { opacity: 0 } : { opacity: 0, y: -6, scale: 0.98 }}
                    transition={{ duration: 0.22, ease: EASE }}
                    className="absolute right-0 top-11 w-56 overflow-hidden rounded-2xl border border-white/15 bg-black/75 p-1.5 text-white shadow-[0_20px_50px_-10px_rgba(0,0,0,0.55)] backdrop-blur-2xl"
                  >
                    <div className="flex items-center justify-between px-3 pb-1.5 pt-2">
                      <p className="font-body text-[10px] uppercase tracking-[0.2em] text-white/60">
                        {localized.share.title}
                      </p>
                      <button
                        type="button"
                        onClick={() => { setShareOpen(false); setPaused(false); }}
                        aria-label="Close"
                        className="press flex h-6 w-6 items-center justify-center rounded-full text-white/60 hover:bg-white/10 hover:text-white"
                      >
                        <X className="h-3.5 w-3.5" strokeWidth={1.6} />
                      </button>
                    </div>
                    <ShareItem onClick={() => openChannel("whatsapp")} testId="share-whatsapp" icon={<WhatsAppGlyph />} label={localized.share.channels.whatsapp} />
                    <ShareItem onClick={() => openChannel("twitter")} testId="share-twitter" icon={<TwitterGlyph />} label={localized.share.channels.twitter} />
                    <ShareItem onClick={() => openChannel("facebook")} testId="share-facebook" icon={<FacebookGlyph />} label={localized.share.channels.facebook} />
                    <div className="my-1 h-px bg-white/10" />
                    <ShareItem onClick={copyShareLink} testId="share-copy" icon={copied ? <Check className="h-4 w-4" strokeWidth={1.8} /> : <Link2 className="h-4 w-4" strokeWidth={1.6} />} label={copied ? localized.share.channels.copied : localized.share.channels.copy} />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* ── Pause / Play toggle ────────────────────────────────────── */}
            <button
              type="button"
              onClick={() => setPaused((p) => !p)}
              aria-label={paused ? localized.aria.play : localized.aria.pause}
              data-testid="story-pause"
              className="press absolute right-3 top-[3.5rem] z-40 flex h-9 w-9 items-center justify-center rounded-full border border-white/25 bg-black/30 text-white shadow-[0_6px_20px_rgba(0,0,0,0.25)] backdrop-blur-xl transition-opacity hover:bg-black/45 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 sm:right-4 sm:top-[4.5rem]"
            >
              {paused ? <PlayIcon className="h-4 w-4 fill-white" strokeWidth={1.5} /> : <Pause className="h-4 w-4 fill-white" strokeWidth={1.5} />}
            </button>

            {/* ── Slides ─────────────────────────────────────────────────── */}
            <motion.div
              className="absolute inset-0 z-10"
              drag="x"
              dragDirectionLock
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.12}
              onDragStart={() => setPaused(true)}
              onDragEnd={onDragEnd}
            >
              <AnimatePresence mode="popLayout" initial={false}>
                <motion.div
                  key={index}
                  className="absolute inset-0"
                  initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 1.025, filter: "blur(4px)" }}
                  animate={reduce ? { opacity: 1 } : { opacity: 1, scale: 1, filter: "blur(0px)" }}
                  exit={reduce ? { opacity: 0 } : { opacity: 0, scale: 1.015, filter: "blur(3px)" }}
                  transition={{ duration: TRANSITION_MS / 1000, ease: EASE }}
                >
                  {isVideo ? (
                    <StoryVideo src={current.src} poster={current.poster} videoRef={videoRef} />
                  ) : (
                    <StoryImage src={current.src} paused={paused} reduce={reduce} />
                  )}
                  {/* Subtle gradient behind text only (bottom third) */}
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/55 via-black/15 to-transparent"
                  />
                </motion.div>
              </AnimatePresence>
            </motion.div>

            {/* ── Tap zones (Stories nav) ────────────────────────────────── */}
            <button
              type="button"
              aria-label={localized.aria.prev}
              data-testid="story-prev"
              onClick={() => { prev(); armResume(); }}
              className="absolute inset-y-10 left-0 z-20 w-1/3 focus:outline-none"
            />
            <button
              type="button"
              aria-label={localized.aria.next}
              data-testid="story-next"
              onClick={() => { next(); armResume(); }}
              className="absolute inset-y-10 right-0 z-20 w-1/3 focus:outline-none"
            />

            {/* ── Text block (bottom-left) ───────────────────────────────── */}
            <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[25] p-6 sm:p-8">
              <div className="max-w-md">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={`text-${index}`}
                    initial={reduce ? { opacity: 0 } : { opacity: 0, y: 8 }}
                    animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
                    exit={reduce ? { opacity: 0 } : { opacity: 0 }}
                    transition={{ duration: 0.4, ease: EASE, delay: 0.12 }}
                  >
                    <h3
                      data-testid={`story-headline-${index}`}
                      className="font-serif-display font-medium leading-[1.05] tracking-tight text-white"
                      style={{ fontSize: "clamp(1.9rem, 4.5vw, 2.5rem)" }}
                    >
                      {current.headline}
                    </h3>
                    {current.sub && (
                      <p
                        data-testid={`story-sub-${index}`}
                        className="mt-2 max-w-sm font-body text-[15px] leading-snug text-white/85 sm:text-base"
                      >
                        {current.sub}
                      </p>
                    )}
                    {current.cta && (
                      <button
                        type="button"
                        onClick={() => handleCtaClick("#size-guide")}
                        data-testid={`story-cta-${index}`}
                        className="press pointer-events-auto mt-4 inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 font-body text-sm font-medium text-[#111] transition-transform hover:scale-[1.02]"
                      >
                        {current.cta}
                        <svg viewBox="0 0 20 20" className="h-3.5 w-3.5" fill="none" aria-hidden="true">
                          <path d="M4 10h12M12 5l5 5-5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// ── StoryImage ──────────────────────────────────────────────────────────────
function StoryImage({ src, paused, reduce }) {
  return (
    <div className="absolute inset-0 overflow-hidden">
      <motion.img
        src={src}
        alt=""
        loading="lazy"
        decoding="async"
        draggable={false}
        className="h-full w-full select-none object-cover"
        initial={{ scale: 1.0 }}
        animate={reduce ? { scale: 1.0 } : { scale: paused ? 1.005 : 1.035 }}
        transition={{ duration: 3.2, ease: "linear" }}
      />
    </div>
  );
}

// ── StoryVideo ──────────────────────────────────────────────────────────────
function StoryVideo({ src, poster, videoRef }) {
  return (
    <div className="absolute inset-0 overflow-hidden bg-[#111]">
      <video
        ref={videoRef}
        key={src /* force remount per slide so only one <video> is live */}
        src={src}
        poster={poster}
        preload="auto"
        autoPlay
        muted
        playsInline
        className="h-full w-full object-cover"
      />
    </div>
  );
}

// ── Share menu row ─────────────────────────────────────────────────────────
function ShareItem({ onClick, testId, icon, label }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      data-testid={testId}
      className="press flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left font-body text-sm text-white/95 transition-colors hover:bg-white/10 focus:bg-white/10 focus:outline-none"
    >
      <span className="flex h-6 w-6 items-center justify-center text-white/85">{icon}</span>
      <span className="flex-1">{label}</span>
    </button>
  );
}

// ── Brand glyphs (SVG, no external icon fonts) ─────────────────────────────
function WhatsAppGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
      <path d="M20.52 3.48A11.94 11.94 0 0 0 12.02 0C5.4 0 .04 5.36.04 11.98c0 2.11.55 4.17 1.6 5.98L0 24l6.22-1.62a11.96 11.96 0 0 0 5.8 1.48h.01c6.61 0 11.98-5.36 11.98-11.98 0-3.2-1.25-6.21-3.49-8.4ZM12.02 21.8h-.01a9.83 9.83 0 0 1-5.01-1.37l-.36-.21-3.7.96.99-3.6-.23-.37a9.8 9.8 0 0 1-1.5-5.22c0-5.42 4.41-9.82 9.82-9.82 2.62 0 5.09 1.02 6.94 2.88a9.75 9.75 0 0 1 2.88 6.94c0 5.42-4.41 9.81-9.82 9.81Zm5.38-7.35c-.29-.15-1.75-.86-2.02-.96-.27-.1-.47-.15-.66.15-.2.29-.76.96-.94 1.15-.17.2-.35.22-.64.07-.29-.15-1.24-.46-2.36-1.46-.87-.78-1.46-1.75-1.63-2.04-.17-.29-.02-.44.13-.59.13-.13.29-.35.44-.52.15-.17.19-.29.29-.49.1-.2.05-.37-.02-.52-.07-.15-.66-1.59-.9-2.18-.24-.57-.48-.5-.66-.5-.17-.02-.37-.02-.57-.02-.2 0-.52.07-.79.37-.27.29-1.04 1.02-1.04 2.48 0 1.46 1.06 2.87 1.21 3.07.15.2 2.09 3.19 5.06 4.48.71.31 1.26.5 1.69.63.71.22 1.36.19 1.87.11.57-.08 1.75-.71 2-1.4.25-.68.25-1.27.17-1.4-.07-.13-.27-.2-.56-.35Z"/>
    </svg>
  );
}
function TwitterGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
      <path d="M18.244 2H21l-6.52 7.454L22 22h-6.727l-4.688-6.128L5.2 22H2.44l6.994-8.003L2 2h6.844l4.243 5.6L18.244 2Zm-1.18 18h1.617L7.02 3.9H5.28L17.064 20Z"/>
    </svg>
  );
}
function FacebookGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
      <path d="M13.5 22v-8h2.7l.4-3.1h-3.1V8.9c0-.9.3-1.5 1.6-1.5h1.7V4.6c-.3-.04-1.3-.14-2.5-.14-2.5 0-4.2 1.5-4.2 4.3v2.2H7.4V14h2.7v8h3.4Z"/>
    </svg>
  );
}
